import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Search, Plus, Building2, Users, Globe, Briefcase, Target, ArrowRight,
  MoreHorizontal, Pencil, Trash2, Sparkles, UserPlus, ExternalLink,
  Phone, Mail, Linkedin, MapPin, DollarSign, Factory, Tag,
  FileText, Upload, ChevronRight, CheckCircle2, AlertCircle, Clock, Loader2
} from 'lucide-react';
import api from '../api';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
  converted: { label: 'Converted', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const priorityConfig = {
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700' },
  medium: { label: 'Medium', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700' },
};

export const MarketResearchPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [creating, setCreating] = useState(false);
  const [converting, setConverting] = useState(false);
  const [runningAI, setRunningAI] = useState(false);

  const [formData, setFormData] = useState({
    company_name: '',
    website: '',
    industry: '',
    company_size: '',
    annual_revenue: '',
    headquarters_location: '',
    priority: 'medium',
    interests: '',
    notes: '',
    contacts: [],
  });

  const [newContact, setNewContact] = useState({
    name: '',
    title: '',
    email: '',
    phone: '',
    linkedin_url: '',
    is_decision_maker: false,
  });

  useEffect(() => {
    fetchProjects();
  }, [filterStatus]);

  const fetchProjects = async () => {
    try {
      const params = {};
      if (filterStatus !== 'all') {
        params.status = filterStatus;
      }
      const response = await api.get('/market-research', { params });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Failed to load market research projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!formData.company_name.trim()) {
      toast.error('Company name is required');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...formData,
        interests: formData.interests.split(',').map(i => i.trim()).filter(Boolean),
      };
      
      await api.post('/market-research', payload);
      toast.success('Market research project created!');
      setShowCreateDialog(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleAddContact = () => {
    if (!newContact.name.trim()) {
      toast.error('Contact name is required');
      return;
    }
    setFormData(prev => ({
      ...prev,
      contacts: [...prev.contacts, { ...newContact }]
    }));
    setNewContact({
      name: '',
      title: '',
      email: '',
      phone: '',
      linkedin_url: '',
      is_decision_maker: false,
    });
  };

  const handleRemoveContact = (index) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts.filter((_, i) => i !== index)
    }));
  };

  const handleConvertToLead = async (projectId) => {
    setConverting(true);
    try {
      const response = await api.post(`/market-research/${projectId}/convert-to-lead`);
      toast.success('Project converted to Cold Call Prospect!');
      setShowDetailDialog(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to lead');
    } finally {
      setConverting(false);
    }
  };

  const handleRunAIResearch = async (projectId) => {
    setRunningAI(true);
    try {
      await api.post(`/market-research/${projectId}/ai-research`);
      toast.success('AI research task created! Check AI Workforce for results.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start AI research');
    } finally {
      setRunningAI(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      await api.delete(`/market-research/${projectId}`);
      toast.success('Project deleted');
      setShowDetailDialog(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const resetForm = () => {
    setFormData({
      company_name: '',
      website: '',
      industry: '',
      company_size: '',
      annual_revenue: '',
      headquarters_location: '',
      priority: 'medium',
      interests: '',
      notes: '',
      contacts: [],
    });
    setNewContact({
      name: '',
      title: '',
      email: '',
      phone: '',
      linkedin_url: '',
      is_decision_maker: false,
    });
  };

  const viewProject = async (project) => {
    try {
      const response = await api.get(`/market-research/${project.id}`);
      setSelectedProject(response.data);
      setShowDetailDialog(true);
    } catch (error) {
      toast.error('Failed to load project details');
    }
  };

  const filteredProjects = projects.filter(p => 
    p.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: projects.length,
    inProgress: projects.filter(p => p.status === 'in_progress').length,
    completed: projects.filter(p => p.status === 'completed').length,
    converted: projects.filter(p => p.status === 'converted').length,
  };

  return (
    <div className="p-6 space-y-6" data-testid="market-research-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Market Research</h1>
          <p className="text-muted-foreground">Research and qualify prospects for your sales pipeline</p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-research-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Research Project
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-3xl font-bold">{stats.total}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Search className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="text-3xl font-bold">{stats.inProgress}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Research Completed</p>
                <p className="text-3xl font-bold">{stats.completed}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Converted to Leads</p>
                <p className="text-3xl font-bold">{stats.converted}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <ArrowRight className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company name or industry..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="converted">Converted</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
              <Search className="h-12 w-12 mb-4 text-slate-300" />
              <p>No research projects found</p>
              <Button 
                variant="link" 
                onClick={() => setShowCreateDialog(true)}
                className="mt-2"
              >
                Create your first project
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Contacts</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProjects.map((project) => (
                  <TableRow 
                    key={project.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => viewProject(project)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold">
                          {project.company_name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium">{project.company_name}</p>
                          {project.website && (
                            <a 
                              href={project.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Globe className="h-3 w-3" />
                              {project.website.replace(/^https?:\/\//, '').split('/')[0]}
                            </a>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-normal">
                        {project.industry || 'Not specified'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span>{project.contacts?.length || 0}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={priorityConfig[project.priority]?.color || priorityConfig.medium.color}>
                        {priorityConfig[project.priority]?.label || 'Medium'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusConfig[project.status]?.color || statusConfig.draft.color}>
                        {statusConfig[project.status]?.label || 'Draft'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => viewProject(project)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRunAIResearch(project.id)}>
                            <Sparkles className="h-4 w-4 mr-2" />
                            AI Research
                          </DropdownMenuItem>
                          {project.status !== 'converted' && (
                            <DropdownMenuItem onClick={() => handleConvertToLead(project.id)}>
                              <ArrowRight className="h-4 w-4 mr-2" />
                              Convert to Lead
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => handleDeleteProject(project.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Project Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Market Research Project</DialogTitle>
            <DialogDescription>
              Add a new company to research for potential sales opportunities
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="company" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="company">Company Info</TabsTrigger>
              <TabsTrigger value="contacts">Contacts ({formData.contacts.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="company" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    placeholder="e.g., Acme Corporation"
                    value={formData.company_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    placeholder="https://www.example.com"
                    value={formData.website}
                    onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Input
                    id="industry"
                    placeholder="e.g., Technology, Healthcare"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company_size">Company Size</Label>
                  <Select 
                    value={formData.company_size} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, company_size: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1-10">1-10 employees</SelectItem>
                      <SelectItem value="11-50">11-50 employees</SelectItem>
                      <SelectItem value="51-200">51-200 employees</SelectItem>
                      <SelectItem value="201-500">201-500 employees</SelectItem>
                      <SelectItem value="501-1000">501-1000 employees</SelectItem>
                      <SelectItem value="1000+">1000+ employees</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="annual_revenue">Annual Revenue</Label>
                  <Input
                    id="annual_revenue"
                    placeholder="e.g., $10M - $50M"
                    value={formData.annual_revenue}
                    onChange={(e) => setFormData(prev => ({ ...prev, annual_revenue: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="headquarters_location">Headquarters</Label>
                  <Input
                    id="headquarters_location"
                    placeholder="e.g., San Francisco, CA"
                    value={formData.headquarters_location}
                    onChange={(e) => setFormData(prev => ({ ...prev, headquarters_location: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="interests">Interests (comma-separated)</Label>
                <Input
                  id="interests"
                  placeholder="e.g., Digital Transformation, Cloud Solutions, AI"
                  value={formData.interests}
                  onChange={(e) => setFormData(prev => ({ ...prev, interests: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Any additional notes about this prospect..."
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                />
              </div>
            </TabsContent>

            <TabsContent value="contacts" className="space-y-4 mt-4">
              {/* Existing Contacts */}
              {formData.contacts.length > 0 && (
                <div className="space-y-2">
                  <Label>Added Contacts</Label>
                  <div className="space-y-2">
                    {formData.contacts.map((contact, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                            <Users className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">{contact.title || 'No title'}</p>
                          </div>
                          {contact.is_decision_maker && (
                            <Badge variant="outline" className="text-xs">Decision Maker</Badge>
                          )}
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => handleRemoveContact(index)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add New Contact */}
              <div className="border rounded-lg p-4 space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  Add Contact
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Name *</Label>
                    <Input
                      id="contact_name"
                      placeholder="John Doe"
                      value={newContact.name}
                      onChange={(e) => setNewContact(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_title">Title</Label>
                    <Input
                      id="contact_title"
                      placeholder="VP of Sales"
                      value={newContact.title}
                      onChange={(e) => setNewContact(prev => ({ ...prev, title: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      placeholder="john@example.com"
                      value={newContact.email}
                      onChange={(e) => setNewContact(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_phone">Phone</Label>
                    <Input
                      id="contact_phone"
                      placeholder="+1 (555) 123-4567"
                      value={newContact.phone}
                      onChange={(e) => setNewContact(prev => ({ ...prev, phone: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_linkedin">LinkedIn URL</Label>
                  <Input
                    id="contact_linkedin"
                    placeholder="https://linkedin.com/in/johndoe"
                    value={newContact.linkedin_url}
                    onChange={(e) => setNewContact(prev => ({ ...prev, linkedin_url: e.target.value }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newContact.is_decision_maker}
                      onChange={(e) => setNewContact(prev => ({ ...prev, is_decision_maker: e.target.checked }))}
                      className="rounded border-gray-300"
                    />
                    <span className="text-sm">Decision Maker</span>
                  </label>
                  <Button type="button" onClick={handleAddContact} variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleCreateProject} disabled={creating}>
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {selectedProject.company_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <DialogTitle className="text-xl">{selectedProject.company_name}</DialogTitle>
                      <DialogDescription className="flex items-center gap-2">
                        {selectedProject.website && (
                          <a 
                            href={selectedProject.website} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <Globe className="h-3 w-3" />
                            {selectedProject.website}
                          </a>
                        )}
                      </DialogDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={statusConfig[selectedProject.status]?.color}>
                      {statusConfig[selectedProject.status]?.label}
                    </Badge>
                    <Badge className={priorityConfig[selectedProject.priority]?.color}>
                      {priorityConfig[selectedProject.priority]?.label}
                    </Badge>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Company Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Factory className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Industry:</span>
                    <span className="text-sm font-medium">{selectedProject.industry || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Size:</span>
                    <span className="text-sm font-medium">{selectedProject.company_size || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Revenue:</span>
                    <span className="text-sm font-medium">{selectedProject.annual_revenue || 'Not specified'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Location:</span>
                    <span className="text-sm font-medium">{selectedProject.headquarters_location || 'Not specified'}</span>
                  </div>
                </div>

                {/* Interests */}
                {selectedProject.interests?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      Interests
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedProject.interests.map((interest, i) => (
                        <Badge key={i} variant="outline">{interest}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* AI Summary */}
                {selectedProject.ai_research_summary && (
                  <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2 text-violet-700">
                      <Sparkles className="h-4 w-4" />
                      AI Research Summary
                    </h4>
                    <p className="text-sm text-slate-700">{selectedProject.ai_research_summary}</p>
                  </div>
                )}

                {/* Contacts */}
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Contacts ({selectedProject.contacts?.length || 0})
                  </h4>
                  {selectedProject.contacts?.length > 0 ? (
                    <div className="space-y-2">
                      {selectedProject.contacts.map((contact, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <Users className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{contact.name}</p>
                              <p className="text-sm text-muted-foreground">{contact.title}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {contact.is_decision_maker && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700">Decision Maker</Badge>
                            )}
                            {contact.email && (
                              <a href={`mailto:${contact.email}`} className="p-2 hover:bg-slate-200 rounded-lg">
                                <Mail className="h-4 w-4 text-muted-foreground" />
                              </a>
                            )}
                            {contact.phone && (
                              <a href={`tel:${contact.phone}`} className="p-2 hover:bg-slate-200 rounded-lg">
                                <Phone className="h-4 w-4 text-muted-foreground" />
                              </a>
                            )}
                            {contact.linkedin_url && (
                              <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-slate-200 rounded-lg">
                                <Linkedin className="h-4 w-4 text-muted-foreground" />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No contacts added yet</p>
                  )}
                </div>

                {/* Notes */}
                {selectedProject.notes && (
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Notes
                    </h4>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                      {selectedProject.notes}
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <div className="flex items-center justify-between w-full">
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => handleDeleteProject(selectedProject.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => handleRunAIResearch(selectedProject.id)}
                      disabled={runningAI}
                    >
                      {runningAI ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4 mr-2" />
                      )}
                      AI Research
                    </Button>
                    {selectedProject.status !== 'converted' && (
                      <Button 
                        onClick={() => handleConvertToLead(selectedProject.id)}
                        disabled={converting}
                      >
                        {converting ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ArrowRight className="h-4 w-4 mr-2" />
                        )}
                        Convert to Lead
                      </Button>
                    )}
                    {selectedProject.converted_to_lead_id && (
                      <Badge className="bg-green-100 text-green-700">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Converted
                      </Badge>
                    )}
                  </div>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MarketResearchPage;
